import { useEffect, useRef, useState, useCallback } from "react";
import { toPng } from "html-to-image";
import { LogoMark, Wordmark } from "@/components/Logo";
import { Download, Loader2, ArrowLeft } from "lucide-react";
import { useNavigate } from "react-router-dom";

const FACE = "/adelaida/smile.jpg";

// Soft-launch promo / "Meet your coach" share screen. Renders Adelaida's exact in-app
// face as a ready-to-post graphic (feed / story / square) with a one-tap PNG download.
const FORMATS = {
  story: { w: 1080, h: 1920, label: "Story / Reel", ratio: "9:16", facePos: "50% 20%" },
  feed: { w: 1080, h: 1350, label: "Feed post", ratio: "4:5", facePos: "50% 22%" },
  square: { w: 1080, h: 1080, label: "Square", ratio: "1:1", facePos: "50% 24%" },
};

const HOOK = "This is how I'll show up for you.";
const SUB = "Your coach, in your corner — every day.";

export default function Promo() {
  const navigate = useNavigate();
  const [fmtKey, setFmtKey] = useState("story");
  const [handle, setHandle] = useState("@xcelio");
  const [scale, setScale] = useState(0.35);
  const [busy, setBusy] = useState(false);
  const frameRef = useRef(null);
  const stageRef = useRef(null);
  const fmt = FORMATS[fmtKey];

  const fit = useCallback(() => {
    const el = stageRef.current;
    if (!el) return;
    const availW = el.clientWidth - 24;
    const availH = el.clientHeight - 24;
    setScale(Math.min(availW / fmt.w, availH / fmt.h, 1));
  }, [fmt.w, fmt.h]);

  useEffect(() => { fit(); const t = setTimeout(fit, 80); window.addEventListener("resize", fit); return () => { clearTimeout(t); window.removeEventListener("resize", fit); }; }, [fit]);

  const download = async () => {
    if (!frameRef.current) return;
    setBusy(true);
    try {
      const dataUrl = await toPng(frameRef.current, { width: fmt.w, height: fmt.h, pixelRatio: 1, cacheBust: true });
      const a = document.createElement("a");
      a.href = dataUrl;
      a.download = `xcelio-${fmtKey}-${fmt.w}x${fmt.h}.png`;
      a.click();
    } catch (e) { console.warn("promo export failed", e); }
    setBusy(false);
  };

  // scale font sizes a touch for the shorter square format
  const big = fmtKey === "square" ? 70 : 82;
  const sub = fmtKey === "square" ? 34 : 40;

  return (
    <div className="min-h-screen flex flex-col" style={{ background: "radial-gradient(120% 90% at 50% 20%, #10182e 0%, #0a0f1e 55%, #060912 100%)" }} data-testid="promo-page">
      {/* header */}
      <div className="flex items-center justify-between px-5 pt-6 pb-3">
        <button onClick={() => navigate(-1)} data-testid="promo-back" className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center text-white/90 hover:bg-white/20 transition-colors">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div className="text-white/90 font-serif text-lg">Meet your coach</div>
        <div className="w-10" />
      </div>

      {/* format tabs */}
      <div className="flex items-center justify-center gap-2 px-5 pb-3">
        {Object.entries(FORMATS).map(([k, f]) => (
          <button key={k} onClick={() => setFmtKey(k)} data-testid={`promo-fmt-${k}`}
            className={`px-4 py-2 rounded-full text-xs font-medium border transition-colors ${fmtKey === k ? "bg-white text-[#0a0f1e] border-white" : "bg-white/5 text-white/70 border-white/15 hover:bg-white/10"}`}>
            {f.label} <span className="opacity-60">· {f.ratio}</span>
          </button>
        ))}
      </div>

      {/* editable handle for the "Try me free" CTA */}
      <div className="flex items-center justify-center px-5 pb-2">
        <input value={handle} onChange={(e) => setHandle(e.target.value)} data-testid="promo-handle"
          placeholder="@yourhandle (optional)" maxLength={30}
          className="bg-white/5 border border-white/15 rounded-full px-4 py-2 text-sm text-white/90 placeholder-white/35 text-center w-56 focus:outline-none focus:border-white/40" />
      </div>
      {/* preview stage */}
      <div ref={stageRef} className="flex-1 flex items-center justify-center px-3 overflow-hidden">
        <div style={{ width: Math.round(fmt.w * scale), height: Math.round(fmt.h * scale), position: "relative" }}>
          <div style={{ position: "absolute", top: 0, left: 0, transform: `scale(${scale})`, transformOrigin: "top left" }}>
          {/* the exact node that gets exported, at true pixel size (no transform on it) */}
          <div ref={frameRef} style={{ width: fmt.w, height: fmt.h, position: "relative", overflow: "hidden", backgroundColor: "#0a0f1e", boxShadow: "0 30px 90px rgba(0,0,0,0.6)" }} data-testid="promo-frame">
            <img src={FACE} alt="Adelaida — your Xcelio coach" crossOrigin="anonymous"
              style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover", objectPosition: fmt.facePos }} />
            {/* brand tint + legibility gradient */}
            <div style={{ position: "absolute", inset: 0, background: "linear-gradient(to bottom, rgba(6,9,18,0.45) 0%, rgba(6,9,18,0.05) 22%, rgba(6,9,18,0.02) 44%, rgba(6,9,18,0.7) 72%, rgba(6,9,18,0.94) 100%)" }} />
            <div style={{ position: "absolute", inset: 0, background: "radial-gradient(120% 80% at 50% 30%, transparent 40%, rgba(6,9,18,0.4) 100%)" }} />

            {/* top brand lockup */}
            <div style={{ position: "absolute", top: 56, left: 60, display: "flex", alignItems: "center", gap: 16 }}>
              <LogoMark size={64} />
              <Wordmark className="text-white" />
            </div>
            {/* top brand wordmark size override via inline (Wordmark uses span) */}
            <style>{`[data-testid="promo-frame"] [aria-label="Xcelio"]{font-size:46px;line-height:1;}`}</style>

            {/* hook copy */}
            <div style={{ position: "absolute", left: 60, right: 60, bottom: fmtKey === "story" ? 150 : 86 }}>
              <div style={{ display: "inline-flex", alignItems: "center", gap: 10, padding: "8px 16px", borderRadius: 999, border: "1px solid rgba(230,200,138,0.5)", background: "rgba(230,200,138,0.1)", marginBottom: 28 }}>
                <span style={{ width: 8, height: 8, borderRadius: 999, background: "#e6c88a" }} />
                <span style={{ color: "#f0ddb4", fontSize: 24, letterSpacing: "0.08em", textTransform: "uppercase", fontWeight: 500 }}>Your personal coach</span>
              </div>
              <p className="font-serif" style={{ color: "#fff", fontSize: big, lineHeight: 1.08, fontWeight: 500, textShadow: "0 2px 24px rgba(0,0,0,0.55)", margin: 0 }}>{HOOK}</p>
              <p style={{ color: "rgba(255,255,255,0.86)", fontSize: sub, lineHeight: 1.3, marginTop: 24, textShadow: "0 2px 18px rgba(0,0,0,0.5)" }}>{SUB}</p>
              {handle.trim() && (
                <div style={{ display: "flex", alignItems: "center", gap: 18, marginTop: 36 }}>
                  <span style={{ background: "#fff", color: "#0a0f1e", fontWeight: 600, fontSize: fmtKey === "square" ? 28 : 32, padding: "14px 30px", borderRadius: 999, whiteSpace: "nowrap" }}>Try me free →</span>
                  <span style={{ color: "#f0ddb4", fontSize: fmtKey === "square" ? 28 : 32, fontWeight: 500 }}>{handle.trim()}</span>
                </div>
              )}
            </div>
          </div>
          </div>
        </div>
      </div>

      {/* actions */}
      <div className="px-5 pb-10 pt-4 flex flex-col items-center gap-3">
        <button onClick={download} disabled={busy} data-testid="promo-download"
          className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-white text-[#0a0f1e] font-medium hover:bg-white/90 transition-colors disabled:opacity-60">
          {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <Download className="w-5 h-5" />}
          Download {fmt.label} ({fmt.w}×{fmt.h})
        </button>
        <p className="text-white/45 text-xs text-center max-w-xs">Saves a crisp PNG ready for Instagram / TikTok. Switch formats above to grab all three.</p>
      </div>
    </div>
  );
}
